import * as React from 'react'
import * as z from 'zod'

import {
  type CustomCell,
  type ProvideEditorCallback,
  type CustomRenderer,
  type Rectangle,
  measureTextCached,
  getMiddleCenterBias,
  GridCellKind,
  getLuminance,
} from '@basehub/glide-data-grid'

import Select, {
  type MenuProps,
  components,
  type StylesConfig,
} from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { BlockType } from '@basehub/data/replicache/index'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { Cross2Icon } from '@radix-ui/react-icons'
import { useTheme } from 'next-themes'
import { LIST_BLOCK_DARK_THEME, LIST_BLOCK_LIGHT_THEME } from '../theme'

export type SelectOption = {
  value: string
  label?: string
  color?: string
  icon?: BlockType
}

const optionSchema = z.union([
  z.string(),
  z.object({
    value: z.string(),
    label: z.string().optional(),
    color: z.string().optional(),
  }),
])

export const multiSelectCellSchema = z.object({
  kind: z.literal('select-cell'),
  values: z.array(z.string()),
  options: z.array(optionSchema).optional(),
  staleOptions: z.array(optionSchema).optional(),
  allowCreation: z.boolean().optional(),
  allowDuplicates: z.boolean().optional(),
  allowMultipleValues: z.boolean().optional(),
})

export type MultiSelectCellProps = z.infer<typeof multiSelectCellSchema>

const SPRITE_SIZE = 14
const MARGIN = 4

/* This prefix is used when allowDuplicates is enabled to make sure that
all underlying values are unique. */
const VALUE_PREFIX = '__value'
const VALUE_PREFIX_REGEX = new RegExp(`^${VALUE_PREFIX}\\d+__`)

/**
 * Prepares the options for usage with the react-select component.
 *
 * @param options The options to prepare.
 * @returns The prepared options in the format required by react-select.
 */
export const prepareOptions = (
  options: readonly (string | SelectOption)[]
): SelectOption[] => {
  return options
    .map((option) => {
      if (!option) return

      if (typeof option === 'string') {
        return {
          value: option,
          label: option || '',
          color: undefined,
        }
      }

      if (!option.value) return

      return {
        value: option.value,
        label: option.label ?? (option.value || ''),
        color: option.color,
        icon: option.icon,
      }
    })
    .filter(Boolean)
}

/**
 * Resolve a list values to values compatible with react-select.
 * If allowDuplicates is true, the values will be prefixed with a numbered prefix to
 * make sure that all values are unique.
 *
 * @param values The values to resolve.
 * @param options The options to use for the resolution.
 * @param allowDuplicates If true, the values can contain duplicates.
 * @returns The list of values compatible with react-select.
 */
export const resolveValues = (
  values: string[] | null | undefined,
  options: readonly SelectOption[],
  allowDuplicates?: boolean
): { value: string; label?: string; color?: string }[] => {
  if (values === undefined || values === null) {
    return []
  }

  return values.map((value, index) => {
    const valuePrefix = allowDuplicates ? `${VALUE_PREFIX}${index}__` : ''
    const matchedOption = options.find((option) => option.value === value)

    if (matchedOption) {
      return {
        ...matchedOption,
        value: `${valuePrefix}${matchedOption.value}`,
      }
    }

    return {
      value,
      label: value,
      type: '',
    }
  })
}

interface CustomMenuProps extends MenuProps<any> {}

const CustomMenu: React.FC<CustomMenuProps> = (p) => {
  const { Menu } = components
  const { children, ...rest } = p
  return <Menu {...rest}>{children}</Menu>
}

export type MultiSelectCell = CustomCell<MultiSelectCellProps>

const Editor: ReturnType<ProvideEditorCallback<MultiSelectCell>> = (p) => {
  const { value: cell, initialValue, onChange, onFinishedEditing } = p
  const {
    options: optionsIn,
    values: valuesIn,
    staleOptions: staleOptionsIn,
    allowCreation,
    allowDuplicates,
    allowMultipleValues,
  } = cell.data

  const theme =
    useTheme().resolvedTheme === 'dark'
      ? LIST_BLOCK_DARK_THEME
      : LIST_BLOCK_LIGHT_THEME
  const [value, setValue] = React.useState(valuesIn)
  const [opts, setOpts] = React.useState(optionsIn)
  const [menuOpen, setMenuOpen] = React.useState(true)
  const [inputValue, setInputValue] = React.useState(initialValue ?? '')

  const options = React.useMemo(() => {
    return prepareOptions(opts ?? [])
  }, [opts])
  const staleOptions = React.useMemo(() => {
    return prepareOptions(staleOptionsIn ?? [])
  }, [staleOptionsIn])

  const menuDisabled = allowCreation && allowDuplicates && options.length === 0

  // Prevent the grid from handling the keydown as long as the menu is open:
  // This allows usage of enter without triggering the grid to finish editing.
  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (menuOpen) {
        e.stopPropagation()
      }
    },
    [menuOpen]
  )

  // Apply styles to the react-select component.
  // All components: https://react-select.com/components
  const selectorStyles: StylesConfig<SelectOption, true> = {
    control: (base) => ({
      ...base,
      border: 0,
      boxShadow: 'none',
      backgroundColor: theme.bgCell,
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: 'var(--color-base-dark)',
      padding: '0 4px',
      borderRadius: 8,
      border: `1px solid ${theme.borderColor}`,
      boxShadow: 'var(--shadow-raw-modal)',
    }),
    option: (styles, state) => {
      return {
        ...styles,
        borderRadius: 4,
        fontSize: 13,
        fontWeight: 400,
        padding: '6px 12px',
        fontFamily: theme.fontFamily,
        color: theme.textDark,
        ...(state.isFocused
          ? { backgroundColor: theme.bgHeaderHovered }
          : { background: 'transparent' }),
        ':active': {
          ...styles[':active'],
          color: theme.textHeader,
          backgroundColor: theme.bgHeaderHovered,
        },
      }
    },
    input: (styles, { isDisabled }) => {
      if (isDisabled) return { display: 'none' }

      return {
        ...styles,
        fontWeight: 400,
        fontSize: theme.editorFontSize,
        fontFamily: theme.fontFamily,
        color: theme.textDark,
      }
    },
    placeholder: (styles) => {
      return {
        ...styles,
        fontSize: theme.editorFontSize,
        fontFamily: theme.fontFamily,
        color: theme.textDark,
      }
    },
    noOptionsMessage: (styles) => {
      return {
        ...styles,
        fontSize: 13,
        padding: '6px 12px',
        fontFamily: theme.fontFamily,
        color: theme.textLight,
      }
    },
    clearIndicator: (styles) => {
      return {
        ...styles,
        color: theme.textLight,
        ':hover': {
          borderRadius: 4,
          backgroundColor: theme.bgHeaderHovered,
          cursor: 'default',
        },
      }
    },
    multiValue: (styles) => {
      return {
        ...styles,
        backgroundColor: 'transparent',
        ':hover': {
          cursor: 'default',
        },
      }
    },
    singleValue: (styles) => {
      return {
        ...styles,
        color: theme.textDark,
        backgroundColor: 'transparent',
        ':hover': {
          cursor: 'default',
        },
      }
    },
    multiValueLabel: (styles, { data }) => {
      return {
        ...styles,
        paddingTop: 0,
        paddingBottom: 0,
        paddingInlineStart: 2,
        color: data.color
          ? // If a color is set for this option,
            // we use it to determine the text color.
            getLuminance(data.color) > 0.5
            ? 'black'
            : 'white'
          : theme.textDark,
        fontSize: theme.editorFontSize,
        fontFamily: theme.fontFamily,
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        height: 20,
      }
    },

    multiValueRemove: (styles, { data, isDisabled }) => {
      if (isDisabled) {
        return {
          display: 'none',
        }
      }

      return {
        ...styles,
        color: data.color
          ? // If a color is set for this option,
            // we use it to determine the text color.
            getLuminance(data.color) > 0.5
            ? 'black'
            : 'white'
          : theme.textBubble,
        backgroundColor: theme.bgHeader,
        ':hover': {
          backgroundColor: theme.bgHeaderHovered,
          cursor: 'default',
        },
      }
    },
  }

  // This is used to submit the values to the grid.
  const submitValues = React.useCallback(
    (values: string[]) => {
      // Change the list of values to the actual values by removing the prefix.
      // This is only relevant in the case of allowDuplicates being true.
      const mappedValues = values.map((v) => {
        return allowDuplicates && v.startsWith(VALUE_PREFIX)
          ? v.replace(new RegExp(VALUE_PREFIX_REGEX), '')
          : v
      })
      setValue(mappedValues)

      if (allowCreation) {
        const valueIsInOptions = mappedValues.some((v) =>
          options.find((o) => o.value === v)
        )

        if (!valueIsInOptions) {
          setOpts([...options, { value: inputValue, label: inputValue }])
        }
      }

      if (!allowMultipleValues) {
        onFinishedEditing({
          ...cell,
          data: { ...cell.data, values: mappedValues },
        })
      } else {
        onChange({
          ...cell,
          data: { ...cell.data, values: mappedValues },
        })
      }
    },
    [
      allowCreation,
      allowMultipleValues,
      allowDuplicates,
      options,
      inputValue,
      onFinishedEditing,
      cell,
      onChange,
    ]
  )

  const handleKeyDown: React.KeyboardEventHandler = (event) => {
    switch (event.key) {
      case 'Escape':
      case 'Tab':
        if (!inputValue) {
          event.stopPropagation()
          event.preventDefault()

          // we finish editing based on the current state.
          const movement: Parameters<typeof onFinishedEditing>[1] =
            event.key === 'Escape' ? [0, 0] : [1, 0]
          onFinishedEditing(cell, movement)
          return
        }

        if (allowDuplicates && allowCreation) {
          // This is a workaround to allow the user to enter new values
          // multiple times.
          setInputValue('')
          submitValues([...(value ?? []), inputValue])
          setMenuOpen(false)
          event.preventDefault()
          event.stopPropagation()
        }
    }
  }

  const SelectComponent = allowCreation ? CreatableSelect : Select
  const _options = [...options, ...staleOptions]

  return (
    <div
      style={{ fontSize: 'var(--gdg-editor-font-size)' }}
      className="flex flex-col items-stretch font-medium bg-dark-base flex-1 overflow-hidden"
      onKeyDown={onKeyDown}
    >
      <SelectComponent
        className="gdg-multi-select"
        isMulti={allowMultipleValues}
        isDisabled={cell.readonly}
        isClearable={true}
        isSearchable={true}
        inputValue={inputValue}
        onInputChange={setInputValue}
        options={options}
        placeholder=""
        noOptionsMessage={(input) => {
          return allowCreation && allowDuplicates && input.inputValue ? (
            <span className="text-dark-dim">
              Create{' '}
              <span className="text-dark-muted">"${input.inputValue}"</span>
            </span>
          ) : undefined
        }}
        menuIsOpen={cell.readonly ? false : menuOpen}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        value={resolveValues(value, _options, allowDuplicates)}
        onKeyDown={cell.readonly ? undefined : handleKeyDown}
        menuPlacement="auto"
        menuPortalTarget={document.getElementById('portal')}
        autoFocus={true}
        openMenuOnFocus={true}
        openMenuOnClick={true}
        closeMenuOnSelect={false}
        backspaceRemovesValue={true}
        escapeClearsValue={false}
        styles={selectorStyles}
        components={{
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
          ClearIndicator: () => null,
          MultiValueRemove: ({ innerProps }) => {
            return (
              <IconButton
                onClick={innerProps.onClick as any}
                onMouseDown={innerProps.onMouseDown as any}
                onTouchEnd={innerProps.onTouchEnd as any}
                className="text-dark-faint hover:text-dark-muted focus:outline focus:outline-dark-control-base focus:outline-offset-1 focus:outline-1 focus:ring-0"
              >
                <Cross2Icon width={12} height={12} />
              </IconButton>
            )
          },
          Menu: (props) => {
            if (menuDisabled) return null

            return (
              <div>
                <CustomMenu className={'click-outside-ignore'} {...props} />
              </div>
            )
          },
        }}
        onChange={async (e) => {
          if (e === null) return

          const submitedValue = Array.isArray(e) ? e : [e]
          const newValues = submitedValue.map((sv) => (sv as any).value)
          submitValues(newValues)
        }}
      />
    </div>
  )
}

export const MultiSelectRenderer: CustomRenderer<MultiSelectCell> = {
  kind: GridCellKind.Custom,
  isMatch: (c): c is MultiSelectCell => (c.data as any).kind === 'select-cell',
  needsHover: false,
  needsHoverPosition: false,
  draw: (args, cell) => {
    const { ctx, theme, rect, spriteManager } = args
    const {
      values,
      options: optionsIn,
      staleOptions: staleOptionsIn,
    } = cell.data

    if (values === undefined || values === null) {
      return true
    }

    const options = prepareOptions([
      ...(optionsIn ?? []),
      ...(staleOptionsIn ?? []),
    ])

    const drawArea: Rectangle = {
      x: rect.x + theme.cellHorizontalPadding,
      y: rect.y + theme.cellVerticalPadding,
      width: rect.width - 2 * theme.cellHorizontalPadding,
      height: rect.height - 2 * theme.cellVerticalPadding,
    }
    const rows = Math.max(1, Math.floor(drawArea.height / SPRITE_SIZE))

    let { x } = drawArea

    const y =
      rows === 1
        ? drawArea.y + (drawArea.height - SPRITE_SIZE) / 2
        : drawArea.y +
          (drawArea.height - rows * SPRITE_SIZE - (rows - 1) * MARGIN) / 2

    values.forEach((value, index) => {
      const matchedOption = options.find((t) => t.value === value)
      const color = 'transparent'
      const displayText = matchedOption?.label ?? value
      const metrics = measureTextCached(displayText, ctx)
      const width = metrics.width
      const textY = SPRITE_SIZE / 2

      ctx.fillStyle = color

      let margin = index === 0 ? 0 : MARGIN

      if (matchedOption?.icon) {
        spriteManager.drawSprite(
          matchedOption.icon,
          'normal',
          ctx,
          x + margin,
          y,
          SPRITE_SIZE,
          { ...args.theme, bgIconHeader: args.theme.textDark }
        )
        margin += MARGIN
      }

      // If a color is set for this option, we use either black or white as the text color depending on the background.
      // Otherwise, use the configured textBubble color.
      ctx.fillStyle = matchedOption?.color
        ? getLuminance(color) > 0.5
          ? theme.accentLight
          : theme.textDark
        : theme.textDark

      ctx.fillText(
        index !== values.length - 1 ? `${displayText},` : displayText,
        matchedOption?.icon ? x + SPRITE_SIZE + margin : x + margin,
        y + textY + getMiddleCenterBias(ctx, theme)
      )

      margin += MARGIN

      if (matchedOption?.icon) x += width + margin + SPRITE_SIZE
      else x += width + margin
    })

    return true
  },
  measure: (ctx, cell, t) => {
    const { values, options } = cell.data

    if (!values) {
      return t.cellHorizontalPadding * 2
    }

    // Resolve the values to the actual display labels:
    const labels = resolveValues(
      values,
      prepareOptions(options ?? []),
      cell.data.allowDuplicates
    ).map((x) => x.label ?? x.value)

    return (
      labels.reduce(
        (acc, data) => ctx.measureText(data).width + acc + MARGIN,
        0
      ) +
      2 * t.cellHorizontalPadding -
      MARGIN
    )
  },
  provideEditor: () => ({
    editor: Editor,
    disablePadding: true,
    deletedValue: (v) => ({
      ...v,
      copyData: '',
      data: {
        ...v.data,
        values: [],
      },
    }),
  }),
  onPaste: (val: string, cell: MultiSelectCellProps) => {
    if (!val || !val.trim()) {
      // Empty values should result in empty strings
      return {
        ...cell,
        values: [],
      }
    }
    let values = val.split(',').map((s) => s.trim())

    if (!cell.allowDuplicates) {
      // Remove all duplicates
      values = values.filter((v, index) => values.indexOf(v) === index)
    }

    if (!cell.allowCreation) {
      // Only allow values that are part of the options:
      const options = prepareOptions(cell.options ?? [])
      values = values.filter((v) => options.find((o) => o.value === v))
    }

    if (values.length === 0) {
      // We were not able to parse any values, return undefined to
      // not change the cell value.
      return undefined
    }
    return { ...cell, values }
  },
}
